import { fg } from '@confluence/feature-gating';
import type { QueryResult, OperationVariables } from '@apollo/react-common';
import {
	type QueryLazyOptions,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- `@confluence/apollo-relay-interop` package is the only one where such imports are allowed
	useLazyQuery,
} from '@apollo/react-hooks';
import isEqual from 'lodash/isEqual';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { type GraphQLTaggedNode, useRelayEnvironment } from 'react-relay';
import { type ConcreteRequest, createOperationDescriptor, getRequest } from 'relay-runtime';
import { type DefinitionNode } from 'graphql';

import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';

import {
	ApolloErrorInterop,
	isApolloError,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback.ts';

import { cloneRelayQueryResult } from './cloneRelayQueryResult.ts';
import { useDeferredQueryNotifications } from './useDeferredQueryNotifications.ts';
import { fetchQuery } from './fetchQuery.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { isNoStoreFetchPolicy, mapFetchPolicyForQuery } from './mapFetchPolicyForQuery.ts';
import { mapFetchPolicyForApollo } from './mapFetchPolicyForApollo.ts';
import { NetworkStatus } from './NetworkStatus.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { useMemoObj } from './useMemoObj.ts';
import { syncQueryCacheBridge } from './cacheBridge/queryCacheBridgeSync.ts';
import { useApolloQueryCacheBridgeSync } from './cacheBridge/useApolloQueryCacheBridgeSync.ts';
import {
	type FetchMoreOptions,
	type FetchMoreType,
	type UseQueryInteropSupportedHookOptions,
} from './useQueryInterop.ts';
import { getRelayOperationName } from './relayOperation.ts';

export type UseLazyQueryInteropSupportedOptions<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
> = UseQueryInteropSupportedHookOptions<TData, TVariables>;

export type UseLazyQueryInteropSupportedReturnType<TData, TVariables> = [
	(options?: QueryLazyOptions<TVariables>) => void,
	UseLazyQueryInteropSupportedResult<TData, TVariables>,
];

export type UseLazyQueryInteropSupportedResult<TData, TVariables> = Pick<
	QueryResult<TData, TVariables>,
	'data' | 'loading' | 'variables' | 'error' | 'called' | 'fetchMore' | 'stopPolling'
>;

/** Synced-each-render ref; no microtask lag. */
function useSyncedRef<T>(value: T) {
	const ref = useRef(value);
	ref.current = value;
	return ref;
}

/**
 * Coerce an unknown thrown value into an ApolloErrorInterop so callers can
 * read Apollo's `graphQLErrors` / `networkError` fields uniformly across the
 * Apollo and Relay branches.
 */
function toApolloError(err: unknown): ApolloErrorInterop {
	if (isApolloError(err as Error)) return err as ApolloErrorInterop;
	const networkError = err instanceof Error ? err : new Error(String(err));
	return new ApolloErrorInterop({ networkError });
}

function getOperationName(node: GraphQLTaggedNode): string {
	if ('definitions' in node) {
		const def = (node as { definitions: readonly DefinitionNode[] }).definitions?.find(
			(d): d is DefinitionNode & { name?: { value: string } } =>
				d.kind === 'OperationDefinition' && 'name' in d,
		);
		return def?.name?.value ?? 'unknown';
	}
	return 'unknown';
}

export function useLazyQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: UseLazyQueryInteropSupportedOptions<TData, TVariables>,
): UseLazyQueryInteropSupportedReturnType<TData, TVariables> {
	const operationName = getRelayOperationName(query);
	if (isRelayEnabled(options?.__callerPackage__, { operationName, phase: 'lazy' })) {
		// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
		return useLazyQueryInteropRelay<TData, TVariables>(query, options);
	}

	// eslint-disable-next-line react-hooks/rules-of-hooks -- Apollo/Relay route is branch-stable for a given caller package.
	return useLazyQueryInteropApollo<TData, TVariables>(query, options);
}

function useLazyQueryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: UseLazyQueryInteropSupportedOptions<TData, TVariables>,
): UseLazyQueryInteropSupportedReturnType<TData, TVariables> {
	const { interopApolloAggClient, ...apolloOptions } = options ?? {};
	// Interop-only policies must be translated before they reach Apollo — see mapFetchPolicyForApollo.
	const apolloFetchPolicy = mapFetchPolicyForApollo(apolloOptions.fetchPolicy);
	const apolloNextFetchPolicy = mapFetchPolicyForApollo(apolloOptions.nextFetchPolicy);
	const {
		getLatestVariables,
		getVariables: getCacheBridgeVariables,
		isEnabled: isCacheBridgeEnabled,
		onCompleted: onApolloCompletedWithCacheBridge,
		syncObservedResult,
		updateLatestVariables,
	} = useApolloQueryCacheBridgeSync<TData, TVariables>({
		query,
		variables: apolloOptions.variables,
		fetchPolicy: apolloFetchPolicy,
		useAggClient: interopApolloAggClient,
		onCompleted: apolloOptions.onCompleted,
		callerPackage: options?.__callerPackage__,
	});
	const onApolloCompleted = isCacheBridgeEnabled
		? onApolloCompletedWithCacheBridge
		: apolloOptions.onCompleted;

	const lazyQueryResult = useLazyQuery<TData, TVariables>(toDocumentNode(query), {
		...apolloOptions,
		fetchPolicy: apolloFetchPolicy,
		...(apolloNextFetchPolicy ? { nextFetchPolicy: apolloNextFetchPolicy } : {}),
		...(onApolloCompleted ? { onCompleted: onApolloCompleted } : {}),
		...(interopApolloAggClient
			? {
					client:
						window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'],
				}
			: {}),
		context: {
			...apolloOptions?.context,
			requestId: (query as ConcreteRequest).params?.id,
		},
	});
	const [executeLazyQuery, apolloResult] = lazyQueryResult;
	const apolloData = apolloResult.data;
	const apolloError = apolloResult.error;
	const apolloCalled = apolloResult.called;
	const apolloVariables = getCacheBridgeVariables(apolloResult.variables);

	useEffect(() => {
		if (!isCacheBridgeEnabled) {
			return;
		}

		updateLatestVariables(apolloVariables);
	}, [apolloVariables, isCacheBridgeEnabled, updateLatestVariables]);

	useEffect(() => {
		if (!isCacheBridgeEnabled) {
			return;
		}

		syncObservedResult({
			skip: !apolloCalled,
			variables: apolloVariables,
			data: apolloData,
			error: apolloError,
		});
	}, [
		apolloCalled,
		apolloData,
		apolloError,
		apolloVariables,
		isCacheBridgeEnabled,
		syncObservedResult,
	]);

	const executeLazyQueryWithCacheBridgeVariables = useCallback(
		(executeOptions?: QueryLazyOptions<TVariables>) => {
			updateLatestVariables(
				(executeOptions?.variables ?? getLatestVariables()) as TVariables | undefined,
			);

			return executeLazyQuery(executeOptions);
		},
		[executeLazyQuery, getLatestVariables, updateLatestVariables],
	);

	if (!isCacheBridgeEnabled) {
		return lazyQueryResult;
	}

	return [executeLazyQueryWithCacheBridgeVariables, apolloResult];
}

function useLazyQueryInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	query: GraphQLTaggedNode,
	options?: UseLazyQueryInteropSupportedOptions<TData, TVariables>,
): UseLazyQueryInteropSupportedReturnType<TData, TVariables> {
	// Validate before any hook call so the throw isn't masked by useRelayEnvironment().
	if (!('params' in query)) {
		throw new Error(
			`[Relay migration] unsupported tagged node provided for operation "${getOperationName(query)}". Please make sure you generated Relay artifacts for your operation`,
		);
	}

	const environment = useRelayEnvironment();
	const operationName = getRelayOperationName(query) ?? undefined;
	const hookVariables = useMemoObj<TVariables>(options?.variables, options?.__callerPackage__, {
		operationName,
		phase: 'memo',
	});
	const hookContext = options?.context;
	// Match Apollo's cache-first default rather than inheriting fetchQuery's
	// network-only default when the caller omits fetchPolicy.
	// See the equivalent comment in useQueryInterop: 'no-store' suppresses store reads as well as
	// writes, and is derived from the initial policy only.
	const noStore = isNoStoreFetchPolicy(options?.fetchPolicy);
	const initialFetchPolicy = mapFetchPolicyForQuery(options?.fetchPolicy);
	const subsequentFetchPolicy = options?.nextFetchPolicy
		? mapFetchPolicyForQuery(options.nextFetchPolicy)
		: initialFetchPolicy;
	const cacheBridgeFetchPolicy = options?.fetchPolicy;
	const cacheBridgeSubsequentFetchPolicy = options?.nextFetchPolicy ?? cacheBridgeFetchPolicy;
	const interopApolloAggClient = options?.interopApolloAggClient;
	const pollInterval = options?.pollInterval ?? 0;
	const notifyOnNetworkStatusChange = options?.notifyOnNetworkStatusChange || false;

	const onError = useStableCallback(options?.onError ?? function noop() {});
	const onCompleted = useStableCallback(options?.onCompleted ?? function noop() {});

	// Defers onCompleted/onError to a post-commit effect so callers observe the state
	// that describes the result being reported, matching Apollo — whose lazy path routes
	// through the same `afterExecute` -> `handleErrorOrCompleted` effect once the query
	// has been run. `queueNotification` is referentially stable, so listing it in the
	// `execute`/`fetchMore` dependency arrays cannot re-issue a request.
	const { queueNotification, markLoading } = useDeferredQueryNotifications<TData>({
		onCompleted,
		onError,
		hasCallbacks: Boolean(options?.onCompleted || options?.onError),
	});

	const [data, setData] = useState<TData | undefined>(undefined);
	const [error, setError] = useState<ApolloErrorInterop | undefined>(undefined);
	const [loading, setLoading] = useState(false);
	const [called, setCalled] = useState(false);
	// Mirrors Apollo's `variables` from the most recent invocation.
	const [activeVariables, setActiveVariables] = useState<TVariables>(hookVariables);
	// True only while a poll tick is the in-flight request. Apollo reports a tick as
	// `networkStatus: poll` rather than `loading`, and the lazy result has no `networkStatus`
	// field, so this flag is what keeps a tick out of the UFO hold below.
	const [isPollTickInFlight, setIsPollTickInFlight] = useState(false);

	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// Subscribe to cache updates after query is called to keep data reactive.
	// This ensures that when mutations update the cache, components using this
	// lazy query will automatically re-render with the fresh data, matching
	// Apollo's behavior where useLazyQuery subscribes to cache changes.
	// See: https://hello.atlassian.net/browse/COPPER-XXXXX (Star menu bug)
	useEffect(() => {
		if (!called) {
			return;
		}

		// 'no-store' writes nothing, so there is no record of its own to watch — and subscribing
		// would surface *other* operations' writes to the same ids, which is the pollution the
		// policy exists to prevent. These queries refetch explicitly instead.
		if (noStore) {
			return;
		}

		// Subscribe to the Relay store to watch for cache updates (e.g., from mutations).
		// This matches the pattern used in useQueryInterop for reactive cache updates.
		const operation = createOperationDescriptor(getRequest(query), activeVariables);
		const retainDisposable = environment.retain(operation);
		const storeSubscription = environment.subscribe(
			environment.lookup(operation.fragment),
			(snapshot) => {
				// Only update if mounted and data is not missing from cache
				// A network payload is normalized before its error policy is checked. Only
				// expose it through execute's success handler while that request is in flight.
				if (
					isMountedRef.current &&
					(!fg('confluence_relay_parity_fixes') || !executeInFlightRef.current) &&
					!snapshot.isMissingData
				) {
					setData(snapshot.data as TData);
				}
			},
		);

		return () => {
			storeSubscription.dispose();
			retainDisposable.dispose();
		};
	}, [called, environment, query, activeVariables, noStore]);

	// Sequencing counters discard out-of-order responses.
	const executeIdRef = useRef(0);
	const failedExecuteRef = useRef<{ query: GraphQLTaggedNode; variables: TVariables }>();
	const fetchMoreIdRef = useRef(0);
	// A poll tick must not race an `execute` for the same query — see `pollTick`.
	const executeInFlightRef = useRef(false);

	// hookContext is unmemoised; data/activeVariables can change mid-fetchMore via a concurrent execute.
	const hookContextRef = useSyncedRef(hookContext);
	const dataRef = useSyncedRef(data);
	const activeVarsRef = useSyncedRef(activeVariables);
	const mirrorRelayResultToApollo = useCallback(
		(
			relayResult: TData | undefined,
			resultVariables: TVariables,
			fetchPolicy: UseLazyQueryInteropSupportedOptions<TData, TVariables>['fetchPolicy'],
		) => {
			// Lazy Relay execution remains the original source of truth; mirror
			// successful payloads into Apollo with the variables from that run.
			syncQueryCacheBridge({
				source: 'relay',
				query,
				variables: resultVariables,
				data: relayResult,
				fetchPolicy,
				useAggClient: interopApolloAggClient,
			});
		},
		[interopApolloAggClient, query],
	);

	const ufoContext = useContext(UFOInteractionContext);
	const ufoOperationName = `relay_${(query as ConcreteRequest).params?.name ?? 'unnamed'}`;
	useEffect(() => {
		// A poll tick is excluded: it can repeat indefinitely, so holding the interaction for one
		// would never release. Mirrors useQueryInterop, which excludes `networkStatus: poll`.
		if (called && loading && !isPollTickInFlight) {
			return ufoContext?.hold(ufoOperationName);
		}
	}, [called, loading, isPollTickInFlight, ufoOperationName, ufoContext]);

	const execute = useCallback(
		(executeOptions?: QueryLazyOptions<TVariables>) => {
			const variables = (executeOptions?.variables ?? hookVariables) as TVariables;
			const context = { ...hookContextRef.current, ...executeOptions?.context };

			// First execute uses fetchPolicy, re-invocations use nextFetchPolicy.
			const isFirstCall = executeIdRef.current === 0;
			const cacheBridgeResultFetchPolicy = isFirstCall
				? cacheBridgeFetchPolicy
				: cacheBridgeSubsequentFetchPolicy;
			executeIdRef.current += 1;
			// Invalidate any in-flight fetchMore so it can't merge into fresh execute data.
			fetchMoreIdRef.current += 1;
			const id = executeIdRef.current;
			const isStillCurrent = () => isMountedRef.current && id === executeIdRef.current;

			const requestedFetchPolicy = isFirstCall ? initialFetchPolicy : subsequentFetchPolicy;
			// Relay normalizes partial payloads before errorPolicy rejects them. A retry of that
			// failed request must reach the network instead of treating the partial cache as success.
			const retryingFailedRequest =
				failedExecuteRef.current?.query === query &&
				isEqual(failedExecuteRef.current.variables, variables);
			const fetchPolicy =
				requestedFetchPolicy === 'store-or-network' &&
				retryingFailedRequest &&
				fg('confluence_relay_parity_fixes')
					? 'network-only'
					: requestedFetchPolicy;
			// A cache hit must not briefly hide existing UI while the resolved promise settles.
			const hasCachedResult = fg('confluence_relay_parity_fixes')
				? !noStore &&
					fetchPolicy === 'store-or-network' &&
					environment.check(createOperationDescriptor(getRequest(query), variables)).status ===
						'available'
				: false;
			setCalled(true);
			setActiveVariables(variables);
			setLoading(fg('confluence_relay_parity_fixes') ? !hasCachedResult : true);
			setError(undefined);
			// Every execute is a fresh loading transition, so a repeat execute with identical
			// variables still reports — matching Apollo, whose dedup guard is bypassed by the
			// loading flag going true.
			markLoading();
			executeInFlightRef.current = true;

			fetchQuery<{ response: TData; variables: TVariables }>(
				environment,
				query,
				variables,
				{
					fetchPolicy,
					networkCacheConfig: { metadata: context },
				},
				{ noStore },
			)
				.toPromise()
				.then((result) => {
					// Cleared even when unmounted, and only by the latest execute, so a superseded
					// response cannot open the door for a poll tick while a newer request is in flight.
					if (id === executeIdRef.current) {
						executeInFlightRef.current = false;
					}
					if (!isStillCurrent()) return;
					setLoading(false);
					if (result !== undefined) {
						failedExecuteRef.current = undefined;
						mirrorRelayResultToApollo(result as TData, variables, cacheBridgeResultFetchPolicy);
						// Relay lookups can allocate equal results. Preserve Apollo's stable data
						// identity so cache-hit executes do not retrigger data-dependent effects.
						if (fg('confluence_relay_parity_fixes')) {
							setData((previous) => (isEqual(previous, result) ? previous : (result as TData)));
						} else {
							setData(result as TData);
						}
						setError(undefined);
						queueNotification({
							type: 'completed',
							data: result as TData,
							identity: { query, variables },
						});
						return;
					}
					// Store-only cache miss: surface so callers aren't left indeterminate.
					const err = toApolloError(
						new Error(
							'[Relay migration] fetchQuery resolved with no data (likely a store-only cache miss)',
						),
					);
					setError(err);
					queueNotification({ type: 'error', error: err, identity: { query, variables } });
				})
				.catch((rawErr: unknown) => {
					if (id === executeIdRef.current) {
						executeInFlightRef.current = false;
					}
					if (!isStillCurrent()) return;
					const err = toApolloError(rawErr);
					failedExecuteRef.current = { query, variables };
					setError(err);
					setLoading(false);
					queueNotification({ type: 'error', error: err, identity: { query, variables } });
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refs (hookContextRef) have stable identity, omitted intentionally
		[
			environment,
			query,
			hookVariables,
			initialFetchPolicy,
			subsequentFetchPolicy,
			cacheBridgeFetchPolicy,
			cacheBridgeSubsequentFetchPolicy,
			mirrorRelayResultToApollo,
			queueNotification,
			markLoading,
			noStore,
		],
	);

	const fetchMore: FetchMoreType<TData, TVariables> = useCallback(
		(fetchMoreOpts: FetchMoreOptions<TData, TVariables>) => {
			const baseVariables = activeVarsRef.current;
			const variables = {
				...baseVariables,
				...fetchMoreOpts.variables,
			} as TVariables;

			fetchMoreIdRef.current += 1;
			const id = fetchMoreIdRef.current;
			const executeId = executeIdRef.current;
			const isStillCurrent = () => isMountedRef.current && id === fetchMoreIdRef.current;

			setLoading(true);

			return fetchQuery<{ response: TData; variables: TVariables }>(
				environment,
				query,
				variables,
				{
					// fetchMore must always hit the network (matches Apollo); cached pages would be stale.
					fetchPolicy: 'network-only',
					networkCacheConfig: { metadata: hookContextRef.current },
				},
				{ noStore },
			)
				.toPromise()
				.then((fetchMoreResult) => {
					// Apollo tears down the previous lazy observable on execute. Its pending
					// fetchMore rejects without calling updateQuery on the replacement query.
					if (executeId !== executeIdRef.current && fg('confluence_relay_parity_fixes')) {
						throw new Error(
							'[Relay migration] fetchMore was superseded by a new lazy query execution',
						);
					}
					// Throw so .catch handles it (avoids double onError).
					if (fetchMoreResult === undefined) {
						throw new Error(
							'[Relay migration] fetchMore resolved with no data (likely a store-only cache miss)',
						);
					}
					const pageData = fg('confluence_relay_parity_fixes')
						? cloneRelayQueryResult(fetchMoreResult as TData)
						: (fetchMoreResult as TData);
					const merged = fetchMoreOpts.updateQuery(
						fg('confluence_relay_parity_fixes')
							? cloneRelayQueryResult(dataRef.current ?? ({} as TData))
							: (dataRef.current ?? ({} as TData)),
						{
							fetchMoreResult: pageData,
							variables,
						},
					);
					// Pages from the same execution merge in completion order, as in Apollo.
					// Keep the execute-generation guard so new executions still invalidate old pages.
					if (
						fg('confluence_relay_parity_fixes')
							? isMountedRef.current && executeId === executeIdRef.current
							: isStillCurrent()
					) {
						if (fg('confluence_relay_parity_fixes')) dataRef.current = merged;
						mirrorRelayResultToApollo(merged, baseVariables, cacheBridgeSubsequentFetchPolicy);
						setData(merged);
						setLoading(false);
						setError(undefined);
					}
					return {
						data: fg('confluence_relay_parity_fixes') ? pageData : merged,
						loading: false,
						networkStatus: NetworkStatus.ready,
						stale: false,
					};
				})
				.catch((rawErr: unknown) => {
					// Deliberately does NOT report via onError. Measured against real Apollo
					// (useLazyQuery + fetchMore, success and failure, either
					// notifyOnNetworkStatusChange): neither callback is invoked — the promise
					// rejection is the only signal. Reporting here would over-fire relative to
					// Apollo and disagree with useQueryInterop.fetchMore, which reports nothing.
					// Error state is still surfaced so the hook's `error` field stays accurate.
					const err = toApolloError(rawErr);
					if (isStillCurrent()) {
						setError(err);
						setLoading(false);
					}
					throw err;
				});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refs (activeVarsRef, dataRef, hookContextRef) have stable identity, omitted intentionally
		[environment, query, cacheBridgeSubsequentFetchPolicy, mirrorRelayResultToApollo, noStore],
	);

	// ── Polling ──────────────────────────────────────────────────────────────────────────────
	// Apollo's lazy queries DO poll, contrary to what this hook previously assumed. `pollInterval`
	// reaches `client.watchQuery`, and `ObservableQuery.setUpQuery` starts the timer as soon as the
	// observable is subscribed — which for `useLazyQuery` is the first `execute`, since
	// `QueryData.execute` short-circuits until `runLazy` is true. Dropping the option left callers
	// with a single request and a promise that never resolves: the report/long-task call sites poll
	// a `ConfluenceLongTask` until it reports success, so one `InProgress` response stalled them
	// forever with no error to show for it.

	// A generation counter invalidates ticks that a `stopPolling()` or an effect teardown has
	// superseded, so an in-flight tick can never schedule its successor after being cancelled.
	const pollGenerationRef = useRef(0);
	const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// The variables that were active when the caller last called `stopPolling()`. Apollo's
	// `stopPolling` is sticky: it clears `options.pollInterval` on the observable, and polling only
	// resumes once the *prepared* options change — i.e. a later `execute` with different variables
	// (`QueryData.updateObservableQuery` -> `ObservableQuery.setOptions` -> `startPolling`). A bare
	// re-execute with identical variables leaves polling stopped, so the guard is deep-compared
	// against the active variables rather than just cleared on the next execute.
	const pollStoppedVariablesRef = useRef<TVariables | null>(null);
	// Declared ahead of `pollTick` so a tick can schedule its successor without referencing its own
	// binding; re-pointed at the freshly-closed-over `pollTick` on every render below.
	const pollTickRef = useRef<(interval: number, generation: number) => void>(() => {});

	const cancelScheduledPoll = useCallback(() => {
		pollGenerationRef.current += 1;
		if (pollTimeoutRef.current !== null) {
			clearTimeout(pollTimeoutRef.current);
			pollTimeoutRef.current = null;
		}
	}, []);

	const stopPolling = useCallback(() => {
		pollStoppedVariablesRef.current = activeVarsRef.current;
		cancelScheduledPoll();
	}, [cancelScheduledPoll, activeVarsRef]);

	// Redefined each render rather than memoised, and reached only through the synced ref below, so
	// a tick always closes over the current environment/variables/callbacks with no stale-closure
	// window and no dependency array that could re-arm the timer.
	const pollTick = (interval: number, generation: number) => {
		const isCurrentGeneration = () =>
			isMountedRef.current && generation === pollGenerationRef.current;
		const scheduleNextTick = () => {
			if (!isCurrentGeneration()) {
				return;
			}
			// setTimeout chained off the previous tick's settlement, not setInterval: Apollo
			// reschedules in `poll()` only after the fetch resolves or rejects
			// (`QueryManager.startPollingQuery`), so a response slower than the interval can never
			// stack requests.
			pollTimeoutRef.current = setTimeout(
				() => pollTickRef.current(interval, generation),
				interval,
			);
		};

		// Apollo skips a tick outright while a request for the query is already in flight and just
		// reschedules (`maybeFetch` -> `checkInFlight`).
		if (executeInFlightRef.current) {
			scheduleNextTick();
			return;
		}

		const variables = activeVarsRef.current;
		if (notifyOnNetworkStatusChange) {
			setIsPollTickInFlight(true);
			setLoading(true);
		}

		fetchQuery<{ response: TData; variables: TVariables }>(
			environment,
			query,
			variables,
			{
				// Apollo forces `network-only` on every tick regardless of the query's own policy
				// (`startPollingQuery` overrides `info.options.fetchPolicy`) — a poll that could be
				// served from the cache would never observe the change it is polling for.
				fetchPolicy: 'network-only',
				networkCacheConfig: { metadata: hookContextRef.current },
			},
			{ noStore },
		)
			.toPromise()
			.then((result) => {
				if (!isMountedRef.current) return;
				if (notifyOnNetworkStatusChange) {
					setIsPollTickInFlight(false);
					setLoading(false);
				}
				if (!isCurrentGeneration()) return;
				// Guard against empty completions so a store-only miss can't wipe `data`.
				if (result !== undefined) {
					mirrorRelayResultToApollo(result as TData, variables, cacheBridgeSubsequentFetchPolicy);
					setData(result as TData);
					setError(undefined);
					// `markLoading()` is deliberately NOT called for a tick: the dedup guard in
					// useDeferredQueryNotifications then suppresses this notification for an
					// unchanged operation, which is what makes a tick apply its data without
					// reporting to onCompleted/onError. Same arrangement as useQueryInterop's
					// runPollTick, and the contract callers are documented against.
					queueNotification({
						type: 'completed',
						data: result as TData,
						identity: { query, variables },
					});
				}
				scheduleNextTick();
			})
			.catch((rawErr: unknown) => {
				if (!isMountedRef.current) return;
				if (notifyOnNetworkStatusChange) {
					setIsPollTickInFlight(false);
					setLoading(false);
				}
				if (!isCurrentGeneration()) return;
				const err = toApolloError(rawErr);
				setError(err);
				queueNotification({ type: 'error', error: err, identity: { query, variables } });
				// Apollo keeps polling after a failed tick (`fetchQuery(...).then(poll, poll)`).
				scheduleNextTick();
			});
	};
	pollTickRef.current = pollTick;

	useEffect(() => {
		// Gated on `called`, not mount: Apollo cannot poll before the observable is subscribed, and
		// for a lazy query that is the first `execute`.
		if (!called || pollInterval <= 0) {
			return;
		}
		// Apollo's QueryManager does not poll in `ssrMode`.
		if (typeof window === 'undefined') {
			return;
		}
		if (
			pollStoppedVariablesRef.current !== null &&
			isEqual(pollStoppedVariablesRef.current, activeVariables)
		) {
			return;
		}
		pollStoppedVariablesRef.current = null;
		pollGenerationRef.current += 1;
		const generation = pollGenerationRef.current;
		// First tick lands one interval after the execute, matching Apollo's initial `poll()`.
		pollTimeoutRef.current = setTimeout(
			() => pollTickRef.current(pollInterval, generation),
			pollInterval,
		);
		return cancelScheduledPoll;
		// `activeVariables` is a dependency so a re-execute re-arms the timer from now, as Apollo's
		// `setOptions` -> `startPolling` does when the prepared options change.
	}, [called, pollInterval, activeVariables, cancelScheduledPoll]);

	// Cast: Apollo's overloaded fetchMore is structurally a superset of FetchMoreType.
	return [
		execute,
		{
			data,
			loading,
			error,
			called,
			variables: activeVariables,
			fetchMore,
			stopPolling,
		} as unknown as UseLazyQueryInteropSupportedResult<TData, TVariables>,
	];
}
